import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth, functions } from '@/js/firebase';
import { agencyService } from '@/services/agencyService';
import { companyService } from '@/services/companyService';
import { inviteService } from '@/services/inviteService';
import { logger } from '@/services/loggerService';
import { useUserAuthStore } from '@/stores/userAuthStore';
import { PROJECT_STATUS } from '@/utils/projectUtils';

/**
 * State for the agency portal (/agency/:agencyId/*).
 *
 * Note the agency ID is also its own company ID — `agencies/X` and `companies/X`
 * are the same organization — so the roster query excludes X itself.
 */
export const useAgencyStore = defineStore('agency', () => {
  const agency = ref(null);
  const companies = ref([]);
  const users = ref([]);
  const projects = ref([]);
  // Events and deliverables for the roster's projects, keyed by projectId, so a
  // request row can report where production and post actually stand.
  const eventsByProject = ref({});
  const deliverablesByProject = ref({});
  const loading = ref(false);
  const error = ref(null);

  const agencyId = computed(() => agency.value?.id || null);
  const markupPercent = computed(() => Number(agency.value?.markupPercent) || 0);

  /**
   * What THIS user is allowed to see.
   *
   * `companies` and `projects` hold the agency's whole roster: the queries are
   * agency-wide, and the management screens genuinely need all of it — an admin
   * granting per-company access has to be able to pick a company the grantee
   * cannot see. So the scoping is a lens over that, not a narrower fetch, and
   * every user-facing surface reads the lens.
   *
   * Agency admins carry `agencyAllCompanies`; a scoped member holds only what
   * was granted, in their own companies[].
   */
  const userAuthStore = useUserAuthStore();

  const accessibleCompanyIds = computed(
    () =>
      new Set(
        (userAuthStore.user?.companies || [])
          .map((c) => (typeof c === 'string' ? c : c?.companyId))
          .filter(Boolean),
      ),
  );

  const seesWholeRoster = computed(
    () => !!userAuthStore.user?.agencyAllCompanies || userAuthStore.isAdmin,
  );

  /** The roster this user may open. Companies/Requests render from this. */
  const visibleCompanies = computed(() =>
    seesWholeRoster.value
      ? companies.value
      : companies.value.filter((c) => accessibleCompanyIds.value.has(c.id)),
  );

  // projects key the company as `company`, NOT `companyId`
  const visibleProjects = computed(() =>
    seesWholeRoster.value
      ? projects.value
      : projects.value.filter((p) => accessibleCompanyIds.value.has(p.company)),
  );

  // The ones actually waiting on the agency — drives the nav badge and the
  // top section of the requests screen.
  const pendingProjects = computed(() =>
    visibleProjects.value.filter((p) => p.status === PROJECT_STATUS.AGENCY_REVIEW),
  );
  const pendingCount = computed(() => pendingProjects.value.length);

  // Everything else, so the agency can see what is in flight without leaving.
  const activeProjects = computed(() =>
    visibleProjects.value.filter((p) => p.status !== PROJECT_STATUS.AGENCY_REVIEW),
  );

  const companyById = computed(() => {
    const map = {};
    for (const c of companies.value) map[c.id] = c;
    return map;
  });

  async function loadAgency(id) {
    loading.value = true;
    error.value = null;
    try {
      const [settings, roster] = await Promise.all([
        agencyService.getAgency(id),
        agencyService.getAgencyCompanies(id, { includeOwn: true }),
      ]);
      agency.value = settings ? { id, ...settings } : null;
      // The agency's own company is included — it is a Vysual company like any
      // other and belongs in the list.
      companies.value = roster;
      return agency.value;
    } catch (err) {
      error.value = err.message;
      logger.error('[AgencyStore] Failed to load agency', { error: err.message, agencyId: id });
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function loadUsers(id) {
    try {
      // Everyone in the agency's own company, not just existing staff — you have
      // to be able to see someone before you can promote them.
      users.value = await agencyService.getAgencyPeople(id);
      return users.value;
    } catch (err) {
      logger.error('[AgencyStore] Failed to load agency users', { error: err.message, agencyId: id });
      throw err;
    }
  }

  /**
   * Every project under this agency's contract, newest first.
   *
   * One indexed query on the agencyId stamped at project create — not a chunked
   * where('company','in',[...]), which caps at 30 values.
   */
  async function loadProjects(id) {
    try {
      const snap = seesWholeRoster.value
        ? await getDocs(query(
            collection(db, 'projects'),
            where('agencyId', '==', id),
            orderBy('dateCreated', 'desc'),
          ))
        : null;

      if (snap) {
        projects.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        return projects.value;
      }

      // A scoped member cannot use the agencyId query: firestore.rules gates
      // that clause on agencyAllCompanies precisely so it can't be used to read
      // the rest of the roster. Their scope is expressed as the query instead —
      // one company equality per company they hold, which the membership clause
      // proves.
      //
      // The agencyId equality rides along to narrow the result, NOT to authorize
      // it: without it this pulls a long-lived client's entire pre-agency
      // history on every queue load. Two equalities need no composite index —
      // Firestore serves them by merging the single-field ones.
      //
      // No orderBy: that would need a composite, and the per-company results
      // have to be merged and sorted here regardless.
      const mine = [...accessibleCompanyIds.value];
      const snaps = await Promise.all(
        mine.map((companyId) => getDocs(query(
          collection(db, 'projects'),
          // projects key the company as `company`, NOT `companyId`
          where('company', '==', companyId),
          where('agencyId', '==', id),
        ))),
      );

      const merged = snaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })));
      const millis = (p) => {
        const ts = p.dateCreated;
        if (ts?.toMillis) return ts.toMillis();
        const date = new Date(ts);
        return Number.isNaN(date.getTime()) ? 0 : date.getTime();
      };
      merged.sort((a, b) => millis(b) - millis(a));

      // The agency's own company drops out for free: its record carries
      // agencyId null, matching the all-companies path, which never sees the
      // agency's own orders either.
      projects.value = merged;
      return projects.value;
    } catch (err) {
      logger.error('[AgencyStore] Failed to load agency projects', {
        error: err.message, agencyId: id, scoped: !seesWholeRoster.value,
      });
      throw err;
    }
  }

  /**
   * Fetch the events and deliverables behind a set of projects.
   *
   * Two different query shapes, because the rules differ: `calendar` is readable
   * by any signed-in user, so it can be filtered by projectId directly;
   * `deliverables` is gated on `companyId in claim`, so a projectId-only query
   * would be rejected outright and it has to go company by company.
   *
   * ponytail: the deliverables pass pulls a company's whole set. Fine at agency
   * scale; if a roster company ever carries thousands, add a composite index on
   * (companyId, projectId) and chunk this the way the calendar pass does.
   */
  async function loadProjectContext(targetProjects) {
    const list = (targetProjects || []).filter((p) => p?.id);
    if (!list.length) {
      eventsByProject.value = {};
      deliverablesByProject.value = {};
      return;
    }

    const projectIds = list.map((p) => p.id);
    const companyIds = [...new Set(list.map((p) => p.company).filter(Boolean))];
    const wanted = new Set(projectIds);

    // Firestore caps an `in` filter at 30 values.
    const chunks = [];
    for (let i = 0; i < projectIds.length; i += 30) chunks.push(projectIds.slice(i, i + 30));

    // Each query is caught on its own: an agency member scoped to some of the
    // roster can READ every roster project (the rules allow that by agencyId)
    // but not every roster company's deliverables. One denial must cost that
    // company's tags, not the whole page's.
    const settle = (label, promise) => promise.catch((err) => {
      logger.warn('[AgencyStore] Skipped project context source', {
        source: label, error: err.message,
      });
      return null;
    });

    try {
      const [eventSnaps, deliverableSnaps] = await Promise.all([
        Promise.all(chunks.map((ids) => settle('calendar', getDocs(query(
          collection(db, 'calendar'),
          where('projectId', 'in', ids),
        ))))),
        Promise.all(companyIds.map((cid) => settle(`deliverables:${cid}`, getDocs(query(
          collection(db, 'deliverables'),
          where('companyId', '==', cid),
        ))))),
      ]);

      const events = {};
      for (const snap of eventSnaps.filter(Boolean)) {
        for (const d of snap.docs) {
          const data = d.data();
          (events[data.projectId] ||= []).push({ id: d.id, ...data });
        }
      }

      const deliverables = {};
      for (const snap of deliverableSnaps.filter(Boolean)) {
        for (const d of snap.docs) {
          const data = d.data();
          if (!wanted.has(data.projectId)) continue;
          (deliverables[data.projectId] ||= []).push({ id: d.id, ...data });
        }
      }

      eventsByProject.value = events;
      deliverablesByProject.value = deliverables;
    } catch (err) {
      // A row without its tags is worth more than a page that fails to load.
      logger.error('[AgencyStore] Failed to load project context', {
        error: err.message, agencyId: agencyId.value,
      });
      eventsByProject.value = {};
      deliverablesByProject.value = {};
    }
  }

  async function createCompany({ logoFile, ...payload }) {
    const fn = httpsCallable(functions, 'createAgencyCompany');
    const { data } = await fn({ agencyId: agencyId.value, ...payload });
    const companyId = data.companyId;

    // The callable granted the caller membership AND wrote the claim inline, but
    // the token in hand predates both — refresh before anything that the rules
    // check against the new company.
    if (data?.claimsRefreshRequired) await refreshClaims();

    // From here the company EXISTS. Nothing below may throw out of this function
    // and leave the caller thinking creation failed: it didn't, and a retry
    // would make a duplicate. Failures are collected and reported instead.
    const warnings = [];

    if (logoFile) {
      try {
        const { createLibraryAsset } = await import('@/services/bunnyUploadService');
        const { LIBRARY_ASSET_TYPE } = await import('@/utils/bunnyConstants');
        const { assetId, url } = await createLibraryAsset({
          file: logoFile,
          companyId,
          companyName: payload.name,
          assetType: LIBRARY_ASSET_TYPE.BRAND_ASSETS,
          tags: ['brand', 'logo'],
        });
        await companyService.updateCompany(
          companyId, { logoUrl: url, logoAssetId: assetId }, false,
        );
      } catch (error) {
        logger.error('[AgencyStore] Logo upload failed', {
          companyId, error: error.message,
        });
        warnings.push('the logo could not be uploaded');
      }
    }

    try {
      await inviteService.sendInvite({
        ...payload.primaryContact,
        companyId,
        companyName: payload.name,
        role: 'user',
        makePrimaryContact: true,
      });
    } catch (error) {
      logger.error('[AgencyStore] Primary contact invite failed', {
        companyId, error: error.message,
      });
      warnings.push('the invite could not be sent');
    }

    await loadAgency(agencyId.value);
    return { companyId, warnings };
  }

  async function inviteTeamMember({ firstName, lastName, email, role, companyIds }) {
    const { inviteId } = await inviteService.sendInvite({
      email,
      firstName,
      lastName,
      companyId: agencyId.value,
      companyName: companyById.value[agencyId.value]?.name || '',
      role: 'user',
      makePrimaryContact: false,
    });

    const fn = httpsCallable(functions, 'attachAgencyInvite');
    await fn({ agencyId: agencyId.value, inviteId, role, companyIds });
    return inviteId;
  }

  /**
   * Set the agency default, or one company's override.
   *
   * @param {number|null} markupPercent - null clears a company override
   * @param {string} [companyId] - omit for the agency default
   */
  async function setMarkup(markupPercent, companyId = null) {
    const fn = httpsCallable(functions, 'setAgencyMarkup');
    await fn({ agencyId: agencyId.value, markupPercent, ...(companyId ? { companyId } : {}) });
    // Reloads the agency AND the roster, so an override shows up either way.
    await loadAgency(agencyId.value);
  }

  async function setUserRole(userId, role) {
    const fn = httpsCallable(functions, 'setAgencyUserRole');
    await fn({ agencyId: agencyId.value, userId, role });
    await loadUsers(agencyId.value);
  }

  async function setUserAccess(userId, companyId, grant) {
    const fn = httpsCallable(functions, 'setAgencyUserAccess');
    await fn({ agencyId: agencyId.value, userId, companyId, grant });
    await loadUsers(agencyId.value);
  }

  async function unlinkCompany(companyId) {
    const fn = httpsCallable(functions, 'unlinkAgency');
    await fn({ companyId });
    await loadAgency(agencyId.value);
  }

  async function refreshClaims() {
    await auth.currentUser?.getIdToken(true);
  }

  function reset() {
    agency.value = null;
    companies.value = [];
    users.value = [];
    projects.value = [];
    eventsByProject.value = {};
    deliverablesByProject.value = {};
    error.value = null;
  }

  return {
    agency, companies, users, projects, pendingProjects, activeProjects, loading, error,
    visibleCompanies, visibleProjects,
    agencyId, markupPercent, pendingCount, companyById,
    eventsByProject, deliverablesByProject,
    loadAgency, loadUsers, loadProjects, loadProjectContext,
    createCompany, inviteTeamMember, setMarkup,
    setUserAccess, setUserRole, unlinkCompany, reset,
  };
});
